import React, { useState, useEffect } from "react";
import { Clock, Check, Plus, X, Users } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Cd, Sec, Inp, Bt, Bg, Sl, SearchSelect } from "../components";
import { WeekPatternEditor, WeekPills, DEFAULT_PATTERN } from "./WeekPatternEditor";

const BLANK_SHIFT = {
  name: "", start_time: "09:00", end_time: "18:00", reminder_time: "",
  late_threshold_minutes: 15, minimum_working_hours: 8, half_day_hours: 4,
  is_flexible: false, flex_daily_min_hours: 6,
  checkin_early_minutes: null, checkin_last_minutes: null,
  late_mark_after_minutes: null, late_mark_status: "",
  schedule: DEFAULT_PATTERN(),
};


// Give one shift to many people at once: everyone in a centre / everyone with no shift yet,
// or exactly the people you search for and pick.
const AssignPanel = ({ shift, offices, onDone }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("centre"); // centre | people
  const [officeId, setOfficeId] = useState("");
  const [onlyFree, setOnlyFree] = useState(true);
  const [people, setPeople] = useState([]);     // [{ id, name }]
  const [pool, setPool] = useState([]);         // all employees for the picker
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && mode === "people" && !pool.length) api.get("/api/employees?limit=1000").then(d => setPool(d?.data || [])).catch(() => {});
  }, [open, mode, pool.length]);
  const body = (extra = {}) => (mode === "people"
    ? { employee_ids: people.map(p => p.id), ...extra }
    : { office_id: officeId || undefined, only_unassigned: onlyFree, ...extra });
  const empId = (e) => e.id || e.employee_id; const empName = (e) => e.nm || e.name || empId(e);
  const options = pool.filter(e => !people.some(p => p.id === empId(e))).map(e => ({ v: empId(e), l: empName(e), sub: empId(e) }));
  const preview = async () => {
    setBusy(true);
    try { setPrev(await api.post(`/api/attendance/shifts/${shift.id}/assign`, body({ preview: true }))); }
    catch (e) { setPrev({ error: e.message || "Couldn't check" }); }
    finally { setBusy(false); }
  };
  const assign = async () => {
    if (!window.confirm(`Give "${shift.name}" to ${prev.count} ${prev.count === 1 ? "person" : "people"}? They will follow this shift's hours and days off.`)) return;
    setBusy(true);
    try { const r = await api.post(`/api/attendance/shifts/${shift.id}/assign`, body()); setPrev(null); setPeople([]); onDone(`"${shift.name}" assigned to ${r.assigned} ${r.assigned === 1 ? "person" : "people"}`); }
    catch (e) { setPrev({ error: e.message || "Couldn't assign" }); }
    finally { setBusy(false); }
  };
  if (!open) return <Bt v="secondary" s="sm" icon={Users} onClick={() => setOpen(true)}>Assign to people</Bt>;
  const tab = (k, l) => <button type="button" onClick={() => { setMode(k); setPrev(null); }} className={`px-3 py-[5px] rounded-lg text-[12.5px] font-bold cursor-pointer border-0 ${mode === k ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}>{l}</button>;
  return (
    <div className="w-full mt-2 p-[10px] rounded-[10px] border border-b0 bg-bg1">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px]">{tab("centre", "By centre")}{tab("people", "Specific people")}</div>
        <Bt v="ghost" s="sm" onClick={() => { setOpen(false); setPrev(null); }}>Close</Bt>
      </div>
      {mode === "centre" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-[240px]"><div className="text-[12px] text-t3 mb-[2px]">Centre</div>
            <Sl options={[{ v: "", l: "All centres" }, ...offices.map(o => ({ v: String(o.id), l: o.name }))]} value={officeId} onChange={v => { setOfficeId(v); setPrev(null); }} /></div>
          <label className="flex items-center gap-2 text-[13px] text-t1 font-semibold pb-[8px] cursor-pointer">
            <input type="checkbox" checked={onlyFree} onChange={e => { setOnlyFree(e.target.checked); setPrev(null); }} /> Only people who have no shift yet
          </label>
          <Bt v="secondary" s="sm" disabled={busy} onClick={preview}>{busy ? "Checking…" : "See who"}</Bt>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-[320px]"><div className="text-[12px] text-t3 mb-[2px]">Search a person and add</div>
              <SearchSelect options={options} value="" onChange={(v) => { const e = pool.find(x => empId(x) === v); if (e) { setPeople(ps => [...ps, { id: v, name: empName(e) }]); setPrev(null); } }} placeholder="Type a name or employee ID…" searchPlaceholder="Search…" /></div>
            <Bt v="secondary" s="sm" disabled={busy || !people.length} onClick={preview}>{busy ? "Checking…" : "See who"}</Bt>
          </div>
          {people.length > 0 && (
            <div className="flex flex-wrap gap-[6px] mt-2">
              {people.map(p => <span key={p.id} className="inline-flex items-center gap-1 pl-[10px] pr-[6px] py-[3px] rounded-full bg-pg text-p text-[12.5px] font-bold">{p.name}
                <button type="button" onClick={() => { setPeople(ps => ps.filter(x => x.id !== p.id)); setPrev(null); }} className="bg-transparent border-0 cursor-pointer text-p p-0 leading-none text-[15px]" aria-label={`Remove ${p.name}`}>×</button></span>)}
            </div>
          )}
        </div>
      )}
      {prev?.error && <div className="text-[13px] text-r mt-2">{prev.error}</div>}
      {prev && !prev.error && (
        <div className="mt-2 text-[13px] text-t1">
          {prev.count === 0 ? (mode === "people" ? "Nobody to change — they already have this shift." : "Nobody matches — everyone there already has a shift.") : <>
            <b>{prev.count}</b> {prev.count === 1 ? "person" : "people"} would get this shift
            {prev.people
              ? <ul className="mt-1 mb-2 pl-4 list-disc">{prev.people.map(p => <li key={p.employee_id}>{p.name} <span className="text-t3">— {p.current_shift ? `now on "${p.current_shift}"` : "no shift yet"}</span></li>)}</ul>
              : <>{prev.sample?.length ? `: ${prev.sample.join(", ")}${prev.count > prev.sample.length ? "…" : ""}` : ""}. </>}
            {prev.requested > prev.count && <div className="text-t3 mb-1">{prev.requested - prev.count} of the people you chose are skipped (already on this shift, or an admin account).</div>}
            <Bt s="sm" icon={Check} disabled={busy} onClick={assign}>Assign now</Bt></>}
        </div>
      )}
    </div>
  );
};

// Shift management — admin/hr_head create named work schedules (Morning,
// Evening, ...) with their own start/end time, check-in reminder time,
// and late/half-day/absent thresholds. Employees are assigned a shift
// from their profile (Employees.jsx); attendance.js's getEffectiveSettings
// applies a shift's thresholds instead of the org-wide defaults for
// anyone assigned one, and the check-in reminder job fires each shift's
// reminder at its own configured time.
export const ShiftsCard = ({ canEdit = true, canView = true, canAssign = true, canSeeCoverage = true, canFlex = true }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // id (or "new") -> edited fields
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState("");
  const [offices, setOffices] = useState([]);
  const [coverage, setCoverage] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/api/attendance/shifts').then(d => setShifts(Array.isArray(d) ? d : [])).catch(() => setShifts([])).finally(() => setLoading(false));
  };
  const loadCoverage = () => (canSeeCoverage ? api.get('/api/attendance/shifts/coverage').then(setCoverage).catch(() => {}) : Promise.resolve());
  useEffect(() => { load(); loadCoverage(); api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d?.data || [])).catch(() => {}); }, []);

  const flash = (m, err = false) => { setMsg((err ? "Error: " : "") + m); setTimeout(() => setMsg(""), 3500); };

  const draftFor = (s) => drafts[s.id] ? { ...s, ...drafts[s.id] } : s;
  const setDraft = (id, patch) => setDrafts(p => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  const saveShift = async (s) => {
    const d = draftFor(s);
    setSavingId(s.id);
    try {
      await api.put(`/api/attendance/shifts/${s.id}`, d);
      setDrafts(p => { const n = { ...p }; delete n[s.id]; return n; });
      flash("Shift saved");
      load();
    } catch (e) { flash(e.message || "Failed to save shift", true); }
    finally { setSavingId(null); }
  };

  const deactivate = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? Employees already assigned to it keep using it until reassigned; it just drops out of the assignment picker.`)) return;
    setSavingId(s.id);
    try {
      await api.del(`/api/attendance/shifts/${s.id}`);
      flash("Shift deactivated");
      load();
    } catch (e) { flash(e.message || "Failed to deactivate shift", true); }
    finally { setSavingId(null); }
  };

  const createShift = async () => {
    const d = { ...BLANK_SHIFT, ...(drafts.new || {}) };
    if (!d.name.trim()) { flash("Shift name is required", true); return; }
    setSavingId("new");
    try {
      await api.post('/api/attendance/shifts', d);
      setDrafts(p => { const n = { ...p }; delete n.new; return n; });
      setAdding(false);
      flash("Shift created");
      load();
    } catch (e) { flash(e.message || "Failed to create shift", true); }
    finally { setSavingId(null); }
  };

  const FIELD_ROW = (val, onChange, extra = {}) => (
    <div className="grid grid-cols-2 sm:grid-cols-6 gap-[6px] mb-[8px]">
      <div className="sm:col-span-2">
        <div className="text-[12px] text-t3 mb-[2px]">Name</div>
        <Inp value={val.name} onChange={v => onChange({ name: v })} placeholder="e.g. Morning" />
      </div>
      <div>
        <div className="text-[12px] text-t3 mb-[2px]">Start</div>
        <Inp type="time" value={val.start_time} onChange={v => onChange({ start_time: v })} />
      </div>
      <div>
        <div className="text-[12px] text-t3 mb-[2px]">End</div>
        <Inp type="time" value={val.end_time} onChange={v => onChange({ end_time: v })} />
      </div>
      <div>
        <div className="text-[12px] text-t3 mb-[2px]">Reminder</div>
        <Inp type="time" value={val.reminder_time || ""} onChange={v => onChange({ reminder_time: v })} />
      </div>
      <div>
        <div className="text-[12px] text-t3 mb-[2px]">Late After (min)</div>
        <Inp type="number" value={String(val.late_threshold_minutes)} onChange={v => onChange({ late_threshold_minutes: parseInt(v) || 0 })} />
      </div>
      <div>
        <div className="text-[12px] text-t3 mb-[2px]">Min Hours</div>
        <Inp type="number" value={String(val.minimum_working_hours)} onChange={v => onChange({ minimum_working_hours: parseFloat(v) || 0 })} />
      </div>
      <div>
        <div className="text-[12px] text-t3 mb-[2px]">Half Day Hours</div>
        <Inp type="number" value={String(val.half_day_hours)} onChange={v => onChange({ half_day_hours: parseFloat(v) || 0 })} />
      </div>
      <div className="sm:col-span-3">
        <div className="text-[12px] text-t3 mb-[2px]">Earliest check-in (min before start)</div>
        <Inp type="number" placeholder="blank = any time" value={val.checkin_early_minutes == null ? "" : String(val.checkin_early_minutes)} onChange={v => onChange({ checkin_early_minutes: v === "" ? null : Math.max(0, parseInt(v) || 0) })} />
        <div className="text-[11px] text-t3 mt-[2px]">How early before the shift starts someone may check in; their time counts from check-in. Earlier than this is refused.</div>
      </div>
      <div className="sm:col-span-3">
        <div className="text-[12px] text-t3 mb-[2px]">Last check-in (min after start)</div>
        <Inp type="number" placeholder="blank = any time" value={val.checkin_last_minutes == null ? "" : String(val.checkin_last_minutes)} onChange={v => onChange({ checkin_last_minutes: v === "" ? null : Math.max(0, parseInt(v) || 0) })} />
        <div className="text-[11px] text-t3 mt-[2px]">After this the check-in is refused, the day shows Absent and a regularization is needed. Leave both blank for no limit.</div>
      </div>
      <div className="sm:col-span-3">
        <div className="text-[12px] text-t3 mb-[2px]">Mark the day if check-in is more than (min late)</div>
        <Inp type="number" placeholder="blank = off" value={val.late_mark_after_minutes == null ? "" : String(val.late_mark_after_minutes)} onChange={v => onChange(v === "" ? { late_mark_after_minutes: null, late_mark_status: "" } : { late_mark_after_minutes: Math.max(0, parseInt(v) || 0) })} />
        <div className="text-[11px] text-t3 mt-[2px]">Minutes after the shift start. Leave blank for no rule — a late check-in is then just marked "Late". They can still check in and out as normal.</div>
      </div>
      {val.late_mark_after_minutes != null && <div className="sm:col-span-3">
        <div className="text-[12px] text-t3 mb-[2px]">Then mark the day as</div>
        <Sl options={[{ v: "", l: "No change (just Late)" }, { v: "Half Day", l: "Half Day" }, { v: "Absent", l: "Absent" }]} value={val.late_mark_status || ""} onChange={v => onChange({ late_mark_status: v })} />
        <div className="text-[11px] text-t3 mt-[2px]">Pick what a check-in later than that is counted as.</div>
      </div>}
      <div className="col-span-2 sm:col-span-6 rounded-lg border border-b0 bg-bg2 px-3 py-2">
        <label className="flex items-center gap-2 cursor-pointer text-[14px] font-semibold text-t0">
          <input type="checkbox" disabled={!canFlex} checked={!!val.is_flexible} onChange={e => onChange({ is_flexible: e.target.checked })} />
          Flexible timing
        </label>
        {!canFlex && <div className="text-[12px] text-a mt-[2px]">You don't have permission to change flexible timing (Settings → Permissions).</div>}
        <div className="text-[12px] text-t3 mt-[2px]">
          {val.is_flexible
            ? `Not a fixed number of hours every day: the person must complete the month's total — working days (this shift's weekly pattern, minus holidays and approved-leave days) × ${val.minimum_working_hours || 0} hrs. A day under the daily minimum below is a Half Day (no deduction if they applied half-day leave; under ${val.half_day_hours || 0} hrs it is Absent). If the month's total falls short, half a day's pay is deducted for every missing ${val.minimum_working_hours || 0} hrs.`
            : "Tick to let people spread their hours across the month instead of working the same hours every day."}
        </div>
        {val.is_flexible && (
          <div className="mt-2 w-[200px]" style={canFlex ? undefined : { pointerEvents: 'none', opacity: 0.6 }}>
            <div className="text-[12px] text-t3 mb-[2px]">Daily Minimum Hours</div>
            <Inp type="number" value={String(val.flex_daily_min_hours ?? 6)} onChange={v => onChange({ flex_daily_min_hours: parseFloat(v) || 0 })} />
          </div>
        )}
      </div>
      {extra.trailing}
      <div className="col-span-2 sm:col-span-6">
        <WeekPatternEditor rules={val.schedule || []} onChange={(schedule) => onChange({ schedule })} shiftStart={val.start_time} shiftEnd={val.end_time} defaultOpen={!!extra.openPattern} />
      </div>
    </div>
  );

  return (
    <Cd style={{ marginBottom: 14 }}>
      <div className="flex items-center justify-between mb-[6px]">
        <Sec icon={Clock} title="Shifts" />
        {canEdit && <Bt v="secondary" s="sm" icon={Plus} onClick={() => setAdding(a => !a)}>{adding ? "Cancel" : "Add Shift"}</Bt>}
      </div>
      <div className="text-[14px] text-t2 mb-[10px]">
        Each shift has its own hours, check-in reminder, late/half-day thresholds and a weekly pattern — which days are working, week-off, half day or work from home, and shorter hours on particular days. Assign a shift to an employee from their profile (Settings tab). Everything about a person's working day — hours, reminder, late limits and days off — comes from their shift.
      </div>

      {!canEdit && <div className="mb-3 px-3 py-2 rounded-[10px] bg-bg2 border border-b0 text-[12.5px] text-t2">You can view shifts but not change them.</div>}
      {coverage && canSeeCoverage && (coverage.unassigned > 0
        ? <div className="mb-3 px-3 py-[10px] rounded-[10px] border text-[13px]" style={{ background: K.ab, borderColor: K.ad, color: K.t1 }}>
            <b>{coverage.unassigned} of {coverage.total}</b> active people have no shift yet. Until they get one they follow the built-in default ({coverage.defaults?.start}–{coverage.defaults?.end}, late after {coverage.defaults?.late_after} min, Sunday off, Saturday off unless set on their profile). Use <b>Assign to people</b> on a shift below, or pick a shift in each employee&apos;s profile.
          </div>
        : <div className="mb-3 px-3 py-[10px] rounded-[10px] border text-[13px]" style={{ background: K.gb, borderColor: K.gd, color: K.t1 }}>Everyone ({coverage.total}) has a shift.</div>)}

      {adding && (() => {
        const d = { ...BLANK_SHIFT, ...(drafts.new || {}) };
        return <div className="p-[10px] bg-bg2 rounded-[10px] border border-b0 mb-3">
          {FIELD_ROW(d, patch => setDraft('new', patch), { openPattern: true })}
          <Bt s="sm" icon={Check} disabled={savingId === 'new'} onClick={createShift}>{savingId === 'new' ? 'Creating...' : 'Create Shift'}</Bt>
        </div>;
      })()}

      {loading && <div className="text-center p-5 text-t3 text-[14px]">Loading…</div>}
      {!loading && shifts.length === 0 && !adding && <div className="text-center p-5 text-t3 text-[14px]">No shifts yet — create one, then give it to people with Assign to people or from their profile.</div>}
      {!loading && shifts.map(s => {
        const d = draftFor(s);
        const dirty = !!drafts[s.id];
        return <div key={s.id} className="p-[10px] bg-bg2 rounded-[10px] border border-b0 mb-2" style={{ opacity: s.is_active ? 1 : 0.5 }}>
          <div className="flex items-center gap-[6px] mb-[8px]">
            {!s.is_active && <Bg c={K.t3} sm>Inactive</Bg>}
            <span className="text-[14px] font-extrabold text-t0">{s.name}</span>
            <WeekPills rules={d.schedule || []} />
          </div>
          <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>{FIELD_ROW(d, patch => setDraft(s.id, patch))}</fieldset>
          <div className="flex flex-wrap items-center gap-[6px]">
            {canEdit && <Bt s="sm" icon={Check} disabled={!dirty || savingId === s.id} onClick={() => saveShift(s)}>{savingId === s.id ? "Saving..." : "Save"}</Bt>}
            {canEdit && s.is_active && <Bt v="danger" s="sm" icon={X} disabled={savingId === s.id} onClick={() => deactivate(s)}>Deactivate</Bt>}
            {canAssign && s.is_active && !dirty && <AssignPanel shift={s} offices={offices} onDone={(m) => { flash(m); loadCoverage(); }} />}
          </div>
        </div>;
      })}
      {msg && <div className={`text-[14px] mt-2 ${msg.startsWith("Error") ? "text-r" : "text-g"}`}>{msg}</div>}
    </Cd>
  );
};
